import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSignedUrlByType } from '../utils/fetchSignedUrlByFolderV2';
import { decodeBase64ToHtml } from '../hooks/useSanitizeAndEncode';
import { fetchDataForTextSnippet } from '../utils/aaiFetchTextFromS3TextUrl';
import 'react-h5-audio-player/lib/styles.css';
import '../styles/rhap.css';
import { selectTargetData } from "../slices/aaiRecordingUtilitySlice";
import { getTextSnippetOverride, selectActiveTextSnippetOverride, selectTextSnippetOverrides } from '../slices/charSamplesSlice';

export const ContentTextBox = React.memo(({ src }) => {
  const [contentText, setContentText] = useState("Loading...");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(src);
        const text = await response.text();
        console.log('ContentTextBox fetchData', text);
        setContentText(text);
      } catch (error) {
        console.error('Error fetching text data:', error.message);
        setContentText("Error fetching text data.");
      }
    };

    if (src) {
      fetchData();
    }
  }, [src]);

  const displayText = decodeBase64ToHtml(contentText || "Unknown Text Snippet");

  return (
    <div className="textBoxForHtml" dangerouslySetInnerHTML={{ __html: displayText }} />
  );
});

export const ContentTextBoxNoFile = React.memo(({ contentText }) => {
  return (
    <div className="textBoxForHtml" dangerouslySetInnerHTML={{ __html: contentText }} />
  );
});

const AaiContentTextBox = React.memo(({ contentOverride }) => {
  const dispatch = useDispatch();
  const targetData = useSelector(selectTargetData);
  const userProfile = useSelector((state) => state.user.profile);
  const textSnippetOverride = useSelector(selectActiveTextSnippetOverride);
  const textSnippetOverrides = useSelector(selectTextSnippetOverrides);
  const [fetchedContent, setFetchedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    character: {
      character: { name, uuid } = { name: "Default Name", uuid: "default-uuid" }
    } = {},
    variation = "Default Variation",
    textSnippet = { content: "Default text" },
  } = targetData?.character || {};

  // Fetch text override when component mounts or textSnippet changes
  useEffect(() => {
    const fetchTextOverride = async () => {
      if (!textSnippet?.uuid) {
        return;
      }

      try {
        const userId = userProfile?.data?.userId;
        if (!userId) {
          console.error('User ID not available');
          return;
        }

        // Dispatch the thunk to get the TextSnippetOverride
        await dispatch(getTextSnippetOverride({
          userId,
          uuid: textSnippet.uuid
        }));
      } catch (error) {
        console.error('Error fetching text override:', error);
      }
    };

    fetchTextOverride();
  }, [dispatch, textSnippet?.uuid, userProfile?.data?.userId]);

  // Fetch text content from S3 when component mounts or textSnippet changes
  useEffect(() => {
    const fetchTextContent = async () => {
      if (!textSnippet?.s3TextUrl && !textSnippet?.uuid) {
        return;
      }

      setIsLoading(true);
      try {
        const userId = userProfile?.data?.userId;
        if (!userId) {
          console.error('User ID not available');
          return;
        }

        const content = await fetchDataForTextSnippet(userId, textSnippet);
        if (content) {
          setFetchedContent(content);
        }
      } catch (error) {
        console.error('Error fetching text content:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTextContent();
  }, [textSnippet, userProfile?.data?.userId]);

  // Helper function to check if content is valid (not empty, not default text)
  const isValidContent = (content) => {
    if (!content) return false;
    if (content === "No Content") return false;
    if (content === "Default text") return false;
    if (content.trim() === "") return false;
    return true;
  };

  // Get the content to display, prioritizing contentOverride if provided, then textOverride from Redux store, 
  // then textOverride from textSnippet, then fetched content, then content
  const getDisplayContent = () => {
    if (isValidContent(contentOverride)) {
      return contentOverride;
    }
    if (isValidContent(textSnippetOverride?.textContent)) {
      return textSnippetOverride.textContent;
    }
    if (isValidContent(textSnippet?.textOverride?.textContent)) {
      return textSnippet.textOverride.textContent;
    }
    if (isValidContent(fetchedContent)) {
      return fetchedContent;
    }
    return isValidContent(textSnippet?.content)
      ? textSnippet.content
      : (isLoading ? "Loading..." : "No Content");
  };

  useEffect(() => {
    console.log('AaiContentTextBox targetData', targetData);
    if (isValidContent(contentOverride)) {
      console.log('Using provided contentOverride');
    } else if (isValidContent(textSnippetOverride?.textContent)) {
      console.log('Using TextSnippetOverride from Redux store:', textSnippetOverride.textContent);
    } else if (isValidContent(textSnippet?.textOverride?.textContent)) {
      console.log('Using TextSnippetOverride from textSnippet:', textSnippet.textOverride.textContent);
    } else if (isValidContent(fetchedContent)) {
      console.log('Using fetched content');
    } else {
      console.log('Using default content or loading state');
    }
  }, [targetData, textSnippet, contentOverride, fetchedContent, textSnippetOverride]);

  return (
    <div className='aai_audio_player_container'>
      <div className='aai_player_container'>
        <ContentTextBoxNoFile contentText={getDisplayContent()} />
      </div>
    </div>
  );
});

export default AaiContentTextBox;
